/**
 * Validates that a URL is safe for client-side redirection.
 *
 * This function implements an allowlist-based approach to prevent
 * unvalidated URL redirection vulnerabilities (CWE-601).
 *
 * @param url The URL to validate
 * @returns true if the URL is safe for redirection, false otherwise
 */
export const isValidRedirectUrl = (url: string | null | undefined): boolean => {
    if (!url) {
        return false;
    }

    // Trim whitespace
    const trimmedUrl = url.trim();

    if (!trimmedUrl) {
        return false;
    }

    // Allow relative paths that start with / or #
    // These navigate within the same origin and are safe
    if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('#')) {
        // Reject protocol-relative URLs (e.g., //evil.com)
        if (trimmedUrl.startsWith('//')) {
            return false;
        }

        // Reject URLs with backslash (e.g., /\evil.com) - used to bypass validation
        if (trimmedUrl.includes('\\')) {
            return false;
        }

        // Reject URLs with protocol prefixes (e.g., /https://evil.com)
        if (trimmedUrl.match(/^\/+https?:/i)) {
            return false;
        }
        return true;
    }

    // Reject any URLs that start with a scheme pattern
    // This includes: http:, https:, javascript:, data:, etc.
    if (trimmedUrl.match(/^[a-z][a-z0-9+.-]*:/i)) {
        return false;
    }

    // Allow relative paths without leading /
    // But ensure they don't try to escape via .. patterns that go too far
    // Allow reasonable relative navigation
    if (trimmedUrl.includes('..')) {
        return false;
    }
    return true;
};

/**
 * Sanitizes a URL for safe client-side redirection.
 * If the URL is invalid, returns a safe fallback.
 *
 * @param url The URL to sanitize
 * @param fallback The fallback URL to use if validation fails (defaults to '/')
 * @returns A safe URL for redirection
 */
export const sanitizeRedirectUrl = (
    url: string | null | undefined,
    fallback: string = '/'
): string => {
    if (isValidRedirectUrl(url)) {
        return url!.trim();
    }
    return isValidRedirectUrl(fallback) ? fallback : '/';
};
